import { Box, CircularProgress, FormControl, FormHelperText, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import { PhoneNumberUtil } from 'google-libphonenumber'
import MuiPhoneNumber from 'material-ui-phone-number'
import React, { useEffect, useState } from 'react'
import { useAsyncHandler } from 'react-hooks-async-handlers'
import { useDispatch, useSelector } from 'react-redux'
import { StepComponentProps } from 'react-step-builder'
import authErrorCodes from '../../const/errorCodes'
import phoneAuthErrorCodes from '../../const/phoneAuthErrorCodes'
import stepIcon from '../../images/step-illustration-phone.png'
import analytics from '../../libs/analytics'
import apiErrorUtil from '../../libs/errors/apiErrorUtil'
import navigationUtil from '../../libs/navigationUtil'
import { ReduxState } from '../../reducers'
import FormDispatcher from '../../reducers/form/dispatcher'
import PhoneDispatcher from '../../reducers/phone/dispatcher'
import { ReduxDispatch } from '../../typings/ReduxDispatch'
import NavigationFooter from './common/NavigationFooter'
import OnboardStepper from './common/OnboardStepper'
import useCountryCode from './hooks/useCountryCode'
import useOnboardAction from './hooks/useOnboardAction'

const phoneUtil = PhoneNumberUtil.getInstance()

const localCountryCodes = [
  {
    code: 1,
    localCode: '1',
  },
  {
    code: 972,
    localCode: '0',
  },
  {
    code: 36,
    localCode: '06',
  },
  {
    code: 7,
    localCode: '8',
  },
]

function StepPhone(props: StepComponentProps) {
  const { form } = useSelector((state: ReduxState) => ({
    form: state.form,
  }))

  const [country_code, setCountryCode] = useCountryCode(form.phone_number)
  const [phone_number, setPhoneNumber] = useState(form.phone_number || '')
  const [error, setError] = useState('')
  const dispatch = useDispatch<ReduxDispatch>()

  const handlePhoneChanges = (value: string) => {
    let cleanNumber = value.replace(/[^\d]/g, '')
    let newValue = value
    if (value.length > 7) {
      localCountryCodes.forEach((country) => {
        // sometimes user start number with local style. (code is exists in number already)
        const countryAndLocalRegex = new RegExp(`^${country.code}${country.localCode}`)
        if (countryAndLocalRegex.test(cleanNumber)) {
          newValue = `+${cleanNumber.replace(countryAndLocalRegex, country.code)}`
        }
      })
    }
    // handleBasicValueChanges('phone_number', newValue, this)
    // props.setState('phone_number', newValue)
    setPhoneNumber(newValue)
    if (error) setError('')
  }

  const isValid = (phone: string) => {
    const number = phoneUtil.parse(phone)
    const isOk = phoneUtil.isValidNumber(number)
    if (!isOk) setError('Phone is not valid')
    return isOk
  }

  const checkPhoneAction = useAsyncHandler(async () => {
    try {
      const data = await dispatch(PhoneDispatcher.checkPhone(phone_number))

      if (data?.verification_id) {
        /**
         * @note receiving verification_id is meaning that user already exists.
         * So we need to send auth request to him. (called identity check)
         */

        // we have logic on next page, which will redirect to needed page.
        props.next()

        throw new Error('Identity verification required! Redirecting...')
      }
    } catch (e) {
      const code = apiErrorUtil.getErrorCodeFromApi(e)
      if (code === authErrorCodes.accountAlreadyExists) {
        analytics.sendEvent('page@StepEnd')
        // do not provide email for navigation, because there is no account with email
        navigationUtil.navigateToHrLogin()
      }

      if (code === phoneAuthErrorCodes.countryIsRestricted) {
        setError('Country is not supported at the moment. You can contact support for help')
      }

      throw e
    }
  })

  const onboardAction = useOnboardAction(props.state as any)

  useEffect(() => {
    analytics.sendEvent('page@StepPhone')
  }, [])

  const handlePressNext = async () => {
    analytics.sendEvent('Onboard@PhoneStep.ClickNext', {}, { phone_number })
    if (!isValid(phone_number)) return null

    setError('')

    // Note that phone should be set before other actions, because we might have not safe redirect to another step.
    await dispatch(FormDispatcher.actions.setPhoneNumber(phone_number))
    await checkPhoneAction.execute() // if something wrong with phone, it will crash
    analytics.sendEvent('Onboard@PhoneProvided', {}, { phone_number })

    await onboardAction.execute()
    props.next()
  }

  const errorMessage = error || checkPhoneAction.error

  return (
    <Grid container direction="column">
      <Grid item sx={{ mb: { xs: 3, sm: 4, md: 4 } }}>
        <OnboardStepper activeStep={4} />
      </Grid>
      <Grid item>
        <Grid container direction="row" spacing={5} justifyContent="spaceBetween">
          <Grid item xs={12} sm={12} md={7}>
            <Box>
              <Typography variant="h1">Congratulations!</Typography>
              <Typography variant="h3" sx={{ fontWeight: 400, color: '#000066', mb: 1 }}>
                Your company app is ready. Please input your phone number and we’ll text you a link to your Reaction
                Club app.
              </Typography>
              {/* <Typography variant="subtitle1"> */}
              {/*  Add your phone number and we’ll text you a link to download your mobile app. */}
              {/* </Typography> */}
            </Box>
            <Box sx={{ mt: { xs: 4, sm: 6, md: 8 }, mb: 6 }}>
              <Grid container direction="column" alignItems={'center'}>
                <FormControl required error={!!errorMessage} variant="standard" sx={{ width: '100%', maxWidth: 500 }}>
                  <MuiPhoneNumber
                    autoFocus
                    defaultCountry={country_code}
                    variant="outlined"
                    value={phone_number}
                    label="Mobile phone"
                    onChange={handlePhoneChanges as any} // lib has broken typing
                    error={!!errorMessage}
                  />
                  <FormHelperText>{errorMessage}</FormHelperText>
                </FormControl>
                <Grid container direction="column" alignItems={'center'} sx={{ mt: 4 }}>
                  {(onboardAction.isLoading || checkPhoneAction.isLoading) && <CircularProgress />}
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item md={5} sx={{ mb: 6, display: { xs: 'none', sm: 'none', md: 'block' } }}>
            <img src={stepIcon} alt="Step phone" width={314} height={310} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <NavigationFooter {...props} onPressNext={handlePressNext} />
      </Grid>
    </Grid>
  )
}

export default StepPhone
